import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

export interface MLPerformanceDistributionData {
  // Array of 10 counts, one for each 0.1 range (0.0-0.1, 0.1-0.2, ..., 0.9-1.0)
  rangeCounts: number[];
}

@Injectable({
  providedIn: 'root'
})
export class MLPerformanceDistributionService {
  /**
   * Mock data for ML Performance Distribution
   * Represents counts for each predicted probability range
   */
  private mockData: MLPerformanceDistributionData = {
    rangeCounts: [
      150, // 0.0-0.1
      120, // 0.1-0.2
      100, // 0.2-0.3
      80, // 0.3-0.4
      60, // 0.4-0.5
      90, // 0.5-0.6
      110, // 0.6-0.7
      140, // 0.7-0.8
      180, // 0.8-0.9
      200 // 0.9-1.0
    ]
  };

  /**
   * Alternative mock data sets for testing different scenarios
   */
  private mockDataSets: Record<string, MLPerformanceDistributionData> = {
    'high-performance': {
      rangeCounts: [50, 40, 30, 20, 10, 60, 80, 150, 200, 250]
    },
    'low-performance': {
      rangeCounts: [300, 250, 200, 150, 100, 80, 60, 40, 20, 10]
    },
    balanced: {
      rangeCounts: [100, 110, 120, 130, 140, 130, 120, 110, 100, 90]
    },
    empty: {
      rangeCounts: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
    }
  };

  /**
   * Fetches ML Performance Distribution data
   * @param dataSet Optional parameter to specify which mock data set to use
   * @returns Observable of MLPerformanceDistributionData
   */
  getMLPerformanceDistribution(dataSet?: string): Observable<MLPerformanceDistributionData> {
    const data = dataSet && this.mockDataSets[dataSet] ? this.mockDataSets[dataSet] : this.mockData;

    // Simulate API call with delay
    return of(data).pipe(
      delay(500) // Simulate network delay
    );
  }

  /**
   * Fetches available mock data set names
   * @returns Observable of string array containing available data set names
   */
  getAvailableDataSets(): Observable<string[]> {
    return of(Object.keys(this.mockDataSets));
  }

  /**
   * Updates the default mock data
   * @param newData New ML Performance Distribution data
   */
  updateMockData(newData: MLPerformanceDistributionData): void {
    if (newData.rangeCounts.length === 10) {
      this.mockData = { ...newData };
    } else {
      throw new Error('Invalid data: rangeCounts must contain exactly 10 elements');
    }
  }

  /**
   * Adds a new mock data set
   * @param name Name of the data set
   * @param data ML Performance Distribution data
   */
  addMockDataSet(name: string, data: MLPerformanceDistributionData): void {
    if (data.rangeCounts.length === 10) {
      this.mockDataSets[name] = { ...data };
    } else {
      throw new Error('Invalid data: rangeCounts must contain exactly 10 elements');
    }
  }

  /**
   * Validates if the data structure is correct
   * @param data Data to validate
   * @returns boolean indicating if data is valid
   */
  validateData(data: MLPerformanceDistributionData): boolean {
    return data && Array.isArray(data.rangeCounts) && data.rangeCounts.length === 10 && data.rangeCounts.every(count => typeof count === 'number' && count >= 0);
  }
}
